import { useCallback, useEffect, useState } from "react";

import { STORAGE_KEYS } from "../app_constants";
import { updateBrowserStateVersion } from "../data/tabSync";
import {
  setPendingConversation,
  getAndClearPendingRaw,
} from "../utils/conversationStorage";

import type { ConversationContextForDraw } from "../components/ConversationContent";

type AppView = "entry" | "canvas";

// Project action types for future extensibility
export type ProjectAction =
  | "new"
  | { type: "load"; diagramId: string }
  | "switch"
  | null;

export const useAppView = () => {
  const [currentView, setCurrentView] = useState<AppView>(() => {
    // Check URL for direct canvas access (collaboration links, etc.)
    const isDirectCanvasAccess =
      window.location.hash.includes("#room=") ||
      window.location.hash.includes("#json=") ||
      window.location.search.includes("id=");
    if (isDirectCanvasAccess) {
      return "canvas";
    }
    // Default: Entry. Use sessionStorage (tab-specific) so refresh stays on the same page.
    const lastView =
      typeof window !== "undefined"
        ? window.sessionStorage.getItem("ideaweave:lastView")
        : null;
    return lastView === "canvas" ? "canvas" : "entry";
  });
  const [aiSidebarCollapsed, setAiSidebarCollapsed] = useState(false);
  const [initialAIPrompt, setInitialAIPrompt] = useState<string | undefined>();
  const [initialPlan, setInitialPlan] = useState<string | undefined>();
  /** When set, first generation request sends conversationContext (Summary then Generation). Used only once. */
  const [initialConversationContext, setInitialConversationContext] = useState<
    ConversationContextForDraw | undefined
  >(undefined);
  /** Images attached when user entered Draw Direct from EntryScreen. Used once, then cleared. */
  const [initialDrawImageUrls, setInitialDrawImageUrls] = useState<
    string[] | undefined
  >(undefined);
  /** Files attached when user entered Draw Direct from EntryScreen. Used once, then cleared. */
  const [initialDrawFiles, setInitialDrawFiles] = useState<
    import("../components/AttachFile").AttachedFile[] | undefined
  >(undefined);
  const [isProjectShelfOpen, setIsProjectShelfOpen] = useState(false);

  // Conversation mode: active within entry view (card expansion)
  // Persist to sessionStorage so page refresh stays on conversation
  const [isConversationActive, setIsConversationActive] = useState(() => {
    try {
      return window.sessionStorage.getItem("ideaweave:convActive") === "true";
    } catch {
      return false;
    }
  });
  const [conversationTopic, setConversationTopic] = useState<
    string | undefined
  >(() => {
    try {
      return window.sessionStorage.getItem("ideaweave:convTopic") || undefined;
    } catch {
      return undefined;
    }
  });
  /** Initial images from EntryScreen (not persisted; only for first conversation message) */
  const [conversationInitialImageUrls, setConversationInitialImageUrls] =
    useState<string[] | undefined>(undefined);
  /** Initial files from EntryScreen (not persisted; only for first conversation message) */
  const [conversationInitialFiles, setConversationInitialFiles] = useState<
    import("../components/AttachFile").AttachedFile[] | undefined
  >(undefined);

  const [projectAction, setProjectAction] = useState<ProjectAction>(null);

  // True when user came to canvas via "Go to Draw" from Conversation; enables "Back to Conversation" in sidebar
  const [hasPendingConversationFromEntry, setHasPendingConversationFromEntry] =
    useState(false);

  const clearConversationStorage = useCallback(() => {
    try {
      window.sessionStorage.removeItem("ideaweave:convMessages");
      window.sessionStorage.removeItem("ideaweave:convActive");
      window.sessionStorage.removeItem("ideaweave:convTopic");
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  // Copy current conversation to Pending before clearing (so we can restore from canvas)
  const saveConversationToPending = useCallback(() => {
    try {
      const messages = window.sessionStorage.getItem("ideaweave:convMessages");
      const topic = window.sessionStorage.getItem("ideaweave:convTopic");
      setPendingConversation(messages, topic);
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  // Restore conversation from Pending into active keys and clear Pending
  const restoreConversationFromPending = useCallback(() => {
    try {
      const { messages, topic } = getAndClearPendingRaw();
      if (messages != null) {
        window.sessionStorage.setItem("ideaweave:convMessages", messages);
      }
      if (topic != null) {
        window.sessionStorage.setItem("ideaweave:convTopic", topic);
      }
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  useEffect(() => {
    window.sessionStorage.setItem("ideaweave:lastView", currentView);
  }, [currentView]);

  useEffect(() => {
    try {
      window.sessionStorage.setItem(
        "ideaweave:convActive",
        String(isConversationActive),
      );
      if (conversationTopic) {
        window.sessionStorage.setItem("ideaweave:convTopic", conversationTopic);
      } else {
        window.sessionStorage.removeItem("ideaweave:convTopic");
      }
    } catch {
      // sessionStorage unavailable
    }
  }, [isConversationActive, conversationTopic]);

  useEffect(() => {
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsProjectShelfOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  // "Weave Idea": stay on entry, expand conversation card
  const handleWeaveIdea = useCallback(
    (
      prompt: string,
      initialImageUrls?: string[],
      initialFiles?: import("../components/AttachFile").AttachedFile[],
    ) => {
      setConversationTopic(prompt);
      setConversationInitialImageUrls(
        initialImageUrls?.length ? initialImageUrls : undefined,
      );
      setConversationInitialFiles(
        initialFiles?.length ? initialFiles : undefined,
      );
      setIsConversationActive(true);
    },
    [],
  );

  // "Go to Draw" from conversation → switch to canvas with conversation context (backend runs Summary then Generation)
  const handleGoToDrawFromConversation = useCallback(
    (context: ConversationContextForDraw) => {
      setIsConversationActive(false);
      setConversationInitialImageUrls(undefined);
      setConversationInitialFiles(undefined);
      setInitialAIPrompt(context.topic);
      setInitialPlan(undefined);
      setInitialConversationContext(context);
      setAiSidebarCollapsed(false);
      setProjectAction("new");
      saveConversationToPending();
      clearConversationStorage();
      setHasPendingConversationFromEntry(true);

      try {
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
        updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
      } catch (error) {
        console.error("[IdeaWeave] Failed to clear localStorage:", error);
      }

      setCurrentView("canvas");
    },
    [clearConversationStorage, saveConversationToPending],
  );

  // Back from conversation → collapse card and clear persisted messages
  const handleBackFromConversation = useCallback(() => {
    setIsConversationActive(false);
    setConversationTopic(undefined);
    setConversationInitialImageUrls(undefined);
    setConversationInitialFiles(undefined);
    clearConversationStorage();
  }, [clearConversationStorage]);

  // Back to Conversation from canvas: restore Pending into active keys and switch to entry with conversation expanded
  const handleBackToConversation = useCallback(() => {
    restoreConversationFromPending();
    try {
      const topic = window.sessionStorage.getItem("ideaweave:convTopic");
      setConversationTopic(topic || undefined);
    } catch {
      setConversationTopic(undefined);
    }
    setIsConversationActive(true);
    setHasPendingConversationFromEntry(false);
    setCurrentView("entry");
  }, [restoreConversationFromPending]);

  // "Draw Direct": skip conversation, go straight to canvas with AI prompt
  const handleDrawDirect = useCallback(
    (
      prompt: string,
      initialImageUrls?: string[],
      initialFiles?: import("../components/AttachFile").AttachedFile[],
    ) => {
      setInitialAIPrompt(prompt);
      setInitialPlan(undefined);
      setInitialConversationContext(undefined);
      setInitialDrawImageUrls(
        initialImageUrls?.length ? initialImageUrls : undefined,
      );
      setInitialDrawFiles(
        initialFiles?.length ? initialFiles : undefined,
      );
      setIsConversationActive(false);
      setConversationTopic(undefined);
      setConversationInitialImageUrls(undefined);
      setConversationInitialFiles(undefined);
      setHasPendingConversationFromEntry(false);
      setAiSidebarCollapsed(false);
      setProjectAction("new");

      try {
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
        localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
        updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
      } catch (error) {
        console.error("[IdeaWeave] Failed to clear localStorage:", error);
      }

      setCurrentView("canvas");
    },
    [],
  );

  // Handler for "Start on canvas" button (blank canvas)
  const handleStartCanvas = useCallback(() => {
    setInitialAIPrompt(undefined);
    setInitialPlan(undefined);
    setInitialConversationContext(undefined);
    setIsConversationActive(false);
    setConversationTopic(undefined);
    setHasPendingConversationFromEntry(false);
    setProjectAction("new");

    try {
      localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
      localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
      updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
    } catch (error) {
      console.error("[IdeaWeave] Failed to clear localStorage:", error);
    }

    setCurrentView("canvas");
  }, []);

  // Handler for back button on canvas
  const handleBackToEntry = useCallback(() => {
    setCurrentView("entry");
    setInitialAIPrompt(undefined);
    setInitialPlan(undefined);
    setInitialConversationContext(undefined);
    setIsConversationActive(false);
    setConversationTopic(undefined);
    setHasPendingConversationFromEntry(false);
    clearConversationStorage();
  }, [clearConversationStorage]);

  // Handler for selecting a project from Entry screen (Recent Projects)
  const handleSelectProjectFromEntry = useCallback((diagramId: string) => {
    setHasPendingConversationFromEntry(false);
    setProjectAction({ type: "load", diagramId });
    setCurrentView("canvas");
  }, []);

  // Handler for "New Project" button in project shelf
  const handleNewProject = useCallback(() => {
    setIsProjectShelfOpen(false);
    setProjectAction("new");
    clearConversationStorage();

    try {
      localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
      localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
      updateBrowserStateVersion(STORAGE_KEYS.VERSION_DATA_STATE);
    } catch (error) {
      console.error("[IdeaWeave] Failed to clear localStorage:", error);
    }

    handleBackToEntry();
  }, [handleBackToEntry, clearConversationStorage]);

  const clearProjectAction = useCallback(() => {
    setProjectAction(null);
  }, []);

  const clearInitialConversationContext = useCallback(() => {
    setInitialConversationContext(undefined);
  }, []);

  return {
    currentView,
    aiSidebarCollapsed,
    setAiSidebarCollapsed,
    initialAIPrompt,
    initialPlan,
    initialConversationContext,
    isProjectShelfOpen,
    setIsProjectShelfOpen,
    handleWeaveIdea,
    handleDrawDirect,
    handleStartCanvas,
    handleBackToEntry,
    handleNewProject,
    handleSelectProjectFromEntry,
    handleGoToDrawFromConversation,
    handleBackFromConversation,
    handleBackToConversation,
    isConversationActive,
    conversationTopic,
    conversationInitialImageUrls,
    conversationInitialFiles,
    projectAction,
    clearProjectAction,
    hasPendingConversationFromEntry,
    clearInitialConversationContext,
    initialDrawImageUrls,
    initialDrawFiles,
    setInitialDrawImageUrls,
    setInitialDrawFiles,
  };
};
